import {
  getThumbnailByUrl,
  getVideoDurationInBrowser,
  getVideoUrl,
} from '@edume/bento/videoUtil.browser';

import {injectEndpoints} from '../api/apiSlice';
import {VIDEO_LIBRARY} from '../api/tagTypes';

export interface Video {
  id: number;
  mediaLibraryAssetId: number;
  url: string;
  thumbnailUrl: string;
  filename: string;
  duration: number;
  createdAt: Date;
  isExternal: boolean;
}

type VideoLibraryResponse = Record<'externalVideos' | 'groupVideos', Video[]>;

const extendedApi = injectEndpoints({
  endpoints: (build) => ({
    getVideoLibrary: build.query<VideoLibraryResponse, number>({
      query: (groupId) => ({url: `groups/${groupId}/videos`}),
      providesTags: (result) =>
        result
          ? [
              ...[...result.externalVideos, ...result.groupVideos].map(
                ({id}) => ({
                  type: VIDEO_LIBRARY,
                  id: id,
                })
              ),
              {type: VIDEO_LIBRARY, id: 'LIST'},
            ]
          : [{type: VIDEO_LIBRARY, id: 'LIST'}],
    }),

    addVideoToLibrary: build.mutation<
      unknown,
      {file: File; videoUrl: string; groupId: number}
    >({
      queryFn: async (
        {file, videoUrl, groupId},
        api,
        extraOptions,
        baseQuery
      ) => {
        const url = getVideoUrl(videoUrl);
        const duration = await getVideoDurationInBrowser({file});
        const thumbnailUrl = getThumbnailByUrl({
          source: 'cloudinary',
          url: videoUrl,
        });

        return baseQuery({
          url: '/videoLibrary',
          method: 'POST',
          body: {url, duration, thumbnailUrl, filename: file.name, groupId},
        });
      },
      invalidatesTags: [{type: VIDEO_LIBRARY, id: 'LIST'}],
    }),

    deleteFromVideoLibrary: build.mutation<
      void,
      Record<'groupId' | 'videoId', number>
    >({
      query: ({groupId, videoId}) => ({
        url: `/groups/${groupId}/videos/${videoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        {type: VIDEO_LIBRARY, id: arg.videoId},
      ],
    }),
  }),
});

export const {
  useAddVideoToLibraryMutation,
  useDeleteFromVideoLibraryMutation,
  useGetVideoLibraryQuery,
} = extendedApi;
